import type {
  ServiceCategoriesPayload,
  ServiceCategoryShort,
  ServiceCategoriesResponse,
  ServiceDropoutResponse,
  ServiceShort,
  ServicesListResponse,
} from '@/types/services'
import type { DefaultError, DeleteError, HTTPError } from '@/types/httpError'
import { useApiCall } from '@/composables/useApiCall'
import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'
import type { DeleteSuccessResponse, ItemPayload } from '@/types/main'
import {
  getServiceCategoriesCall,
  deleteServiceCategoryCall,
  getServiceSearchCategoriesCall,
  getServiceSearchDetailsCall,
  getServicesListCall,
  deleteServiceItemCall,
  getServiceSearchCall,
} from '@/api/services'
import type { SelectItem } from '@/types/catalog'

export const useServicesStore = defineStore('services', () => {
  type ServicesStoreEntityType = 'category' | 'service'

  const categories = ref<ServiceCategoryShort[]>([])
  const services = ref<ServiceShort[]>([])

  const metaData = reactive({
    total: 0,
    totalPages: 0,
  })
  const filterFields = reactive({
    page: 1,
    perPage: 15,
  }) as ServiceCategoriesPayload

  const {
    data: serviceCategoriesData,
    executeApiCall: serviceCategoriesAction,
    isLoading: serviceCategoriesLoading,
    error: serviceCategoriesError,
  } = useApiCall<ServiceCategoriesResponse, DefaultError, ServiceCategoriesPayload>(
    getServiceCategoriesCall,
    true,
    filterFields,
  )

  const {
    data: servicesListData,
    executeApiCall: servicesListAction,
    isLoading: servicesListLoading,
    error: servicesListError,
  } = useApiCall<ServicesListResponse, DefaultError, ServiceCategoriesPayload>(
    getServicesListCall,
    true,
    filterFields,
  )

  const isCategoriesLoading = computed(() => serviceCategoriesLoading.value)
  const isServicesListLoading = computed(() => servicesListLoading.value)
  const errorValue = ref<HTTPError<DeleteError> | null>(null)

  const { executeApiCall: deleteCategoryAction, error: deleteCategoryError } = useApiCall<
    DeleteSuccessResponse,
    DeleteError,
    ItemPayload
  >(deleteServiceCategoryCall, true)

  const { executeApiCall: deleteServiceAction, error: deleteServiceError } = useApiCall<
    DeleteSuccessResponse,
    DeleteError,
    ItemPayload
  >(deleteServiceItemCall, true)

  const {
    data: categoriesSelectData,
    executeApiCall: categoriesSelectAction,
    isLoading: categoriesSelectLoading,
  } = useApiCall<ServiceDropoutResponse, DefaultError, { search: string, perPage: number }>(
    getServiceSearchCategoriesCall,
    true,
  )
  const parentCategories = computed(() => categoriesSelectData.value?.data)
  const isParentCategoriesLoading = computed(() => categoriesSelectLoading.value)

  const getParentCategories = async (value?: string) => {
    await categoriesSelectAction({ search: value || '', perPage: 200 })
  }

  const {
    data: servicesSelectData,
    executeApiCall: servicesSelectAction,
    // isLoading: servicesSelectLoading,
  } = useApiCall<ServiceDropoutResponse, DefaultError, { search: string, perPage: number }>(
    getServiceSearchCall,
    true,
  )
  const servicesSelect = computed(() => servicesSelectData.value?.data)
  // const isServicesSelectLoading = computed(() => servicesSelectLoading.value)

  const getServicesSelect = async (value?: string) => {
    await servicesSelectAction({ search: value || '', perPage: 200 })
  }

  const {
    data: detailsSelectData,
    executeApiCall: detailsSelectAction,
    isLoading: detailsSelectLoading,
  } = useApiCall<SelectItem[], DefaultError, { search: string, perPage: number }>(
    getServiceSearchDetailsCall,
    true,
  )
  const detailsSelect = computed(() => detailsSelectData.value)
  const isDetailsSelectLoading = computed(() => detailsSelectLoading.value)

  const getDetailsForDropout = async (value?: string) => {
    await detailsSelectAction({ search: value || '', perPage: 30 })
  }

  const getServicesForDropout = async (value?: string) => {
    await servicesListAction({ search: value || '', perPage: 999, with: 'prices' })
  }

  const deleteServiceCategory = async (id: number | string) => {
    try {
      await deleteCategoryAction({ id })
    } catch {
      if (deleteCategoryError.value) {
        errorValue.value = deleteCategoryError.value
      }
    }
  }

  const deleteServiceItem = async (id: number | string) => {
    try {
      await deleteServiceAction({ id })
    } catch {
      if (deleteServiceError.value) {
        errorValue.value = deleteServiceError.value
      }
    }
  }

  const fetchEntities = async (type: ServicesStoreEntityType) => {
    switch (type) {
      case 'category':
        await getServiceCategories()
        break
      case 'service':
        await getServicesList()
        break
      default:
        break
    }
  }

  const changePage = async (page: number, type: ServicesStoreEntityType) => {
    if (filterFields.page === page) {
      return
    }
    filterFields.page = page
    await fetchEntities(type)
  }

  const clearFilter = async (type: ServicesStoreEntityType) => {
    clearQuery()
    await applyFilter(filterFields, type)
  }

  const applyFilter = async (filters: ServiceCategoriesPayload, type: ServicesStoreEntityType) => {
    Object.keys(filters).forEach(key => {
      if (
        filters[key as keyof ServiceCategoriesPayload]
        || filters[key as keyof ServiceCategoriesPayload] === '0'
        || filters[key as keyof ServiceCategoriesPayload] === false
      ) {
        filterFields[key as keyof ServiceCategoriesPayload]
          = filters[key as keyof ServiceCategoriesPayload]
      } else {
        filterFields[key as keyof ServiceCategoriesPayload] = ''
      }
    })
    filterFields.page = 1
    await fetchEntities(type)
  }

  const applySort = async (value: string | null, type: ServicesStoreEntityType) => {
    if (value) {
      filterFields.sort = value
    } else {
      delete filterFields.sort
    }
    filterFields.page = 1
    await fetchEntities(type)
  }

  const getServiceCategories = async () => {
    try {
      const filter = {} as ServiceCategoriesPayload
      Object.keys(filterFields).forEach(key => {
        if (
          filterFields[key as keyof ServiceCategoriesPayload]
          || filterFields[key as keyof ServiceCategoriesPayload] === false
        ) {
          filter[key as keyof ServiceCategoriesPayload]
            = filterFields[key as keyof ServiceCategoriesPayload]
        }
      })
      await serviceCategoriesAction(filter)
      if (serviceCategoriesData.value) {
        categories.value = [...serviceCategoriesData.value.data]
        metaData.total = serviceCategoriesData.value.pagination.total
        metaData.totalPages = serviceCategoriesData.value.pagination.totalPages
      }
    } catch {
      console.error(serviceCategoriesError.value)
    }
  }

  const getServicesList = async () => {
    try {
      const filter = {} as ServiceCategoriesPayload
      Object.keys(filterFields).forEach(key => {
        if (
          filterFields[key as keyof ServiceCategoriesPayload]
          || filterFields[key as keyof ServiceCategoriesPayload] === false
        ) {
          filter[key as keyof ServiceCategoriesPayload]
            = filterFields[key as keyof ServiceCategoriesPayload]
        }
      })
      await servicesListAction(filter)
      if (servicesListData.value) {
        services.value = [...servicesListData.value.data]
        metaData.total = servicesListData.value.pagination.total
        metaData.totalPages = servicesListData.value.pagination.totalPages
      }
    } catch {
      console.error(servicesListError.value)
    }
  }

  const clearQuery = () => {
    metaData.totalPages = 0
    metaData.total = 0
    filterFields.page = 1
    filterFields.perPage = 15
    categories.value = []
    services.value = []
    errorValue.value = null
    Object.keys(filterFields).forEach(
      n => ['page', 'perPage'].includes(n) || delete filterFields[n],
    )
  }

  const $reset = () => clearQuery()

  return {
    categories,
    services,
    isCategoriesLoading,
    isServicesListLoading,
    getServiceCategories,
    getServicesList,
    metaData,
    filterFields,
    changePage,
    clearQuery,
    applyFilter,
    clearFilter,
    applySort,
    deleteServiceCategory,
    deleteServiceItem,
    errorValue,
    $reset,
    parentCategories,
    isParentCategoriesLoading,
    getParentCategories,
    detailsSelect,
    isDetailsSelectLoading,
    getDetailsForDropout,
    getServicesForDropout,
    servicesListData,
    servicesSelect,
    // isServicesSelectLoading,
    getServicesSelect,
  }
})
